import type { LockSide } from '@planner/shared/doors';
import { LockDialog } from './LockDialog.js';

function HandDiagram({ side, bare = false }: { side: LockSide; bare?: boolean }) {
  const left = side === 'left';
  const handleX = left ? 77 : 143;
  const hingeX = left ? 150 : 66;
  return (
    <svg
      viewBox={bare ? '20 24 180 210' : '0 20 220 222'}
      className={bare ? 'mx-auto h-auto w-24 sm:w-28' : 'mx-auto h-auto w-full max-w-[269px]'}
      role={bare ? undefined : 'img'}
      aria-hidden={bare ? true : undefined}
      aria-label={
        bare
          ? undefined
          : `${side} handle, hinges on the ${left ? 'right' : 'left'}, viewed from outside`
      }
    >
      <rect x="70" y="34" width="80" height="190" fill="white" stroke="#374151" strokeWidth="1.6" />
      <rect x="78" y="42" width="64" height="174" fill="#F3F4F6" stroke="#9CA3AF" />
      {[54, 129, 204].map((y) => (
        <rect key={y} x={hingeX} y={y - 8} width="4" height="16" fill="#374151" />
      ))}
      <rect
        x={handleX - 3.5}
        y="122"
        width="7"
        height="28"
        rx="2"
        fill="white"
        stroke="#374151"
        strokeWidth="1.3"
      />
      <rect
        x={left ? handleX : handleX - 22}
        y="133.5"
        width="22"
        height="5"
        rx="2.5"
        fill="white"
        stroke="var(--brand-progress)"
        strokeWidth="1.3"
      />
      <circle cx={handleX} cy="136" r="2.2" fill="var(--brand-progress)" />
      <circle
        cx={handleX + (left ? 4 : -4)}
        cy="136"
        r="18"
        fill="none"
        stroke="var(--brand-progress)"
        strokeDasharray="3 2"
      />
      {!bare && (
        <>
          <text
            x={left ? 160 : 60}
            y="132"
            fontSize="9.5"
            fill="#374151"
            textAnchor={left ? 'start' : 'end'}
          >
            Hinges
          </text>
          <text
            x={left ? 60 : 160}
            y="152"
            fontSize="9.5"
            fill="#C2410C"
            textAnchor={left ? 'end' : 'start'}
          >
            Handle
          </text>
          <text x="150" y="238" fontSize="8" fill="#9CA3AF" textAnchor="end">
            BOTTOM OF DOOR
          </text>
        </>
      )}
      <line x1="30" y1="224" x2="190" y2="224" stroke="#9CA3AF" />
    </svg>
  );
}

export function LockSideCard({
  side,
  selected,
  invalid,
  onSelect,
}: {
  side: LockSide;
  selected: boolean;
  invalid: boolean;
  onSelect(side: LockSide): void;
}) {
  return (
    <div className="relative">
      <label
        className={`flex h-full cursor-pointer flex-col items-center gap-3 rounded-xl border-2 px-3 py-4 text-center transition-colors ${
          selected
            ? 'border-progress bg-orange-50/20'
            : 'border-gray-200 bg-white hover:border-gray-300'
        } has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-progress`}
      >
        <input
          className="sr-only"
          type="radio"
          name="lock-side"
          data-name={`lock-side-${side}`}
          value={side}
          checked={selected}
          required
          aria-invalid={invalid}
          aria-describedby="lock-side-guidance"
          onChange={() => onSelect(side)}
        />
        <HandDiagram side={side} bare />
        <span
          className={`block text-sm font-semibold capitalize ${selected ? 'text-progress' : 'text-gray-600'}`}
        >
          {side} hand
          {selected && <span className="mt-0.5 block text-[10px]">✓ Selected</span>}
        </span>
      </label>
      <LockDialog
        handing
        name={`lock-${side}`}
        topic={side === 'left' ? 'handing_left' : 'handing_right'}
        title={`${side} hand — viewed from outside`}
        triggerLabel={`Enlarge ${side} hand diagram`}
        triggerName={`lock-enlarge-${side}`}
        triggerClassName="absolute top-2 right-2 flex size-11 items-center justify-center rounded-full text-progress focus-visible:outline-2 focus-visible:outline-progress"
        trigger={
          <span
            aria-hidden="true"
            className="flex size-8 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-gray-200"
          >
            <svg
              className="size-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-4-4M11 8v6M8 11h6" />
            </svg>
          </span>
        }
      >
        {(close) => (
          <>
            <HandDiagram side={side} />
            <p className="mt-5 text-center text-sm font-semibold text-progress">
              Swings out towards you
            </p>
            <p className="mt-1 text-center text-xs leading-relaxed text-gray-400">
              Stand outside your front door and face it. If you want the handle on your {side},
              choose <strong>{side} hand</strong>.
            </p>
            <button
              type="button"
              data-name={`lock-select-${side}`}
              onClick={() => {
                onSelect(side);
                close();
              }}
              className="mt-5 min-h-11 w-full rounded-xl bg-progress px-4 py-2 text-sm font-semibold text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-progress"
            >
              Select {side} hand
            </button>
          </>
        )}
      </LockDialog>
    </div>
  );
}
